import { AxiosInstance } from "axios";
import {
  AdminUser,
  AuditLogEntry,
  EventSummary,
  OrganizerSummary,
  OrderSummary,
  Paginated,
  PayoutSummary,
  UserSummary,
  DashboardPayload,
  DashboardStats,
  NewsPost,
  HelpCategory,
  HelpArticle,
} from "../types/admin";

const unwrapPaginated = <T>(res: any): Paginated<T> => {
  const meta = res.data?.meta;
  const data = (res.data?.data ?? res.data) as T[];
  return {
    data,
    total: meta?.total ?? data.length,
    per_page: meta?.per_page ?? data.length,
    current_page: meta?.current_page ?? 1,
  };
};

const unwrapNestedPaginated = <T>(payload: any): Paginated<T> => {
  const meta = payload?.meta;
  const data = (payload?.data ?? payload) as T[];
  return {
    data,
    total: meta?.total ?? data.length,
    per_page: meta?.per_page ?? data.length,
    current_page: meta?.current_page ?? 1,
  };
};

/**
 * 薄い API クライアント。レスポンス shape はバックエンド仕様に合わせて後で調整する。
 */
export const adminApi = (client: AxiosInstance) => ({
  me: async (): Promise<AdminUser> => {
    const res = await client.get("/me");
    return res.data.user as AdminUser;
  },
  login: async (email: string, password: string): Promise<string | null> => {
    const res = await client.post("/login", { email, password });
    return (res.data?.access_token as string) ?? null;
  },
  logout: () => client.post("/logout"),

  listUsers: async (params?: Record<string, string | number | undefined>): Promise<Paginated<UserSummary>> => {
    const res = await client.get("/users", { params });
    return unwrapPaginated<UserSummary>(res);
  },
  listOrganizers: async (params?: Record<string, string | number | undefined>): Promise<Paginated<OrganizerSummary>> => {
    const res = await client.get("/organizers", { params });
    return unwrapPaginated<OrganizerSummary>(res);
  },
  verifyOrganizer: async (id: string | number, payload: { status: "pending" | "approved" | "rejected"; note?: string | null }) => {
    return client.patch(`/organizers/${id}/verify`, payload);
  },
  updateOrganizerFee: async (id: string | number, payload: { platform_fee_rate: number; payment_fee_rate: number }) => {
    return client.patch(`/organizers/${id}/fee`, payload);
  },
  listEvents: async (params?: Record<string, string | number | undefined>): Promise<Paginated<EventSummary>> => {
    const res = await client.get("/events", { params });
    return unwrapPaginated<EventSummary>(res);
  },
  updateEventStatus: async (id: string | number, status: "draft" | "published") =>
    client.patch(`/events/${id}/status`, { status }),
  listOrders: async (params?: Record<string, string | number | undefined>): Promise<Paginated<OrderSummary>> => {
    const res = await client.get("/orders", { params });
    return unwrapPaginated<OrderSummary>(res);
  },
  listPayouts: async (params?: Record<string, string | number | undefined>): Promise<Paginated<PayoutSummary>> => {
    const res = await client.get("/payouts", { params });
    return unwrapPaginated<PayoutSummary>(res);
  },
  approvePayout: (id: string | number, note?: string) => client.patch(`/payouts/${id}/approve`, note ? { note } : {}),
  rejectPayout: (id: string | number, reason: string) => client.patch(`/payouts/${id}/reject`, { reason }),
  markPayoutPaid: (id: string | number) => client.patch(`/payouts/${id}/mark-paid`),
  listAuditLogs: async (params?: Record<string, string | number | undefined>): Promise<Paginated<AuditLogEntry>> => {
    const res = await client.get("/audit-logs", { params });
    return unwrapPaginated<AuditLogEntry>(res);
  },
  dashboard: async (): Promise<DashboardPayload> => {
    const res = await client.get("/dashboard");
    return {
      stats: res.data.stats as DashboardStats,
      recent_payouts: unwrapNestedPaginated<PayoutSummary>(res.data.recent_payouts),
      recent_orders: unwrapNestedPaginated<OrderSummary>(res.data.recent_orders),
      upcoming_events: unwrapNestedPaginated<EventSummary>(res.data.upcoming_events),
    };
  },
  listNews: async (params?: Record<string, string | number | undefined>): Promise<Paginated<NewsPost>> => {
    const res = await client.get("/news", { params });
    return unwrapPaginated<NewsPost>(res);
  },
  getNews: async (id: number): Promise<NewsPost> => {
    const res = await client.get(`/news/${id}`);
    return (res.data?.data ?? res.data) as NewsPost;
  },
  createNews: async (payload: Partial<NewsPost>) => client.post("/news", payload),
  updateNews: async (id: number, payload: Partial<NewsPost>) => client.patch(`/news/${id}`, payload),
  publishNews: async (id: number) => client.post(`/news/${id}/publish`),
  pinNews: async (id: number, pinned: boolean) => client.post(`/news/${id}/pin`, { pinned }),

  listHelpCategories: async (params?: Record<string, string | number | undefined>): Promise<HelpCategory[]> => {
    const res = await client.get("/help/categories", { params });
    return res.data.data ?? res.data;
  },
  createHelpCategory: async (payload: Partial<HelpCategory>) => client.post("/help/categories", payload),
  updateHelpCategory: async (id: number, payload: Partial<HelpCategory>) => client.patch(`/help/categories/${id}`, payload),

  listHelpArticles: async (params?: Record<string, string | number | undefined>): Promise<Paginated<HelpArticle>> => {
    const res = await client.get("/help/articles", { params });
    return unwrapPaginated<HelpArticle>(res);
  },
  getHelpArticle: async (id: number): Promise<HelpArticle> => {
    const res = await client.get(`/help/articles/${id}`);
    return (res.data?.data ?? res.data) as HelpArticle;
  },
  createHelpArticle: async (payload: Partial<HelpArticle>) => client.post("/help/articles", payload),
  updateHelpArticle: async (id: number, payload: Partial<HelpArticle>) => client.patch(`/help/articles/${id}`, payload),
  publishHelpArticle: async (id: number) => client.post(`/help/articles/${id}/publish`),
});

export type AdminApi = ReturnType<typeof adminApi>;
